import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../AuthContext";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import { fr } from "date-fns/locale";
import { isBefore, startOfDay } from "date-fns";
import {
  CalendarDays,
  CreditCard,
  Lock,
  Sparkles,
  BadgePercent,
  Tag,
  CheckCircle,
  XCircle,
  Loader2,
} from "lucide-react";
import { useToast } from "../../components/ui/Toast";

function PackageSelector({ gameId, isBeta }) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { token } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const toYMD = (d) => {
    if (!d) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };
  const fromYMD = (s) => {
    if (!s) return null;
    const [y, m, d] = s.split("-").map(Number);
    if (!y || !m || !d) return null;
    const dt = new Date(y, m - 1, d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  };

  const initialPack = searchParams.get("packType") || "normal";
  const initialSessions = Number(searchParams.get("sessions") || 1);
  const initialDate = fromYMD(searchParams.get("date"));

  const [pack, setPack] = useState(initialPack);
  const [sessions, setSessions] = useState(
    Number.isFinite(initialSessions) && initialSessions > 0 ? initialSessions : 1
  );
  const [date, setDate] = useState(initialDate);
  const [total, setTotal] = useState(0);

  // ── Coupon ──
  const [couponInput, setCouponInput] = useState("");
  const [couponState, setCouponState] = useState(null); // null | { valid, discount, name } | { error }
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null); // ID du coupon validé

  const API = process.env.REACT_APP_API_URL;

  const computeUnitPrice = (packType, sessionCount) => {
    const isExtended = packType === "extended";
    if (sessionCount >= 10) return isExtended ? 150 : 135;
    if (sessionCount >= 5)  return isExtended ? 160 : 145;
    if (sessionCount >= 2)  return isExtended ? 170 : 155;
    return isExtended ? 185 : 165;
  };

  const buildNext = () =>
    encodeURIComponent(`${location.pathname}?${searchParams.toString()}`);

  const unit = useMemo(() => computeUnitPrice(pack, sessions), [pack, sessions]);
  const baseUnit = useMemo(() => computeUnitPrice(pack, 1), [pack]);

  const savingsPerSession = Math.max(0, baseUnit - unit);
  const savingsTotal = savingsPerSession * sessions;

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("packType", pack);
    next.set("sessions", String(sessions));
    const ymd = toYMD(date);
    if (ymd) next.set("date", ymd);
    else next.delete("date");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pack, sessions, date]);

  useEffect(() => {
    setTotal(unit * sessions);
  }, [unit, sessions]);

  const tierLabel = useMemo(() => {
    if (sessions >= 10) return "Tarif 10+";
    if (sessions >= 5) return "Tarif 5–9";
    if (sessions >= 2) return "Tarif 2–4";
    return "Tarif 1";
  }, [sessions]);

  // ── Vérifier le coupon ──
  const handleCheckCoupon = async () => {
    const trimmed = couponInput.trim();
    if (!trimmed) return;
    setCouponLoading(true);
    setCouponState(null);
    try {
      const storedToken = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/api/stripe/check-coupon`,
        { couponId: trimmed },
        { headers: { Authorization: `Bearer ${storedToken}` } }
      );
      setCouponState({ valid: true, discount: res.data.discount, name: res.data.name });
      setAppliedCoupon(trimmed);
    } catch (e) {
      setCouponState({ error: e.response?.data?.message || "Code promo invalide." });
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponInput("");
    setCouponState(null);
    setAppliedCoupon(null);
  };

  const handleBuy = async () => {
    try {
      const storedToken = localStorage.getItem("token");
      if (!storedToken) {
        navigate(`/login?next=${buildNext()}`);
        return;
      }

      const res = await axios.post(
        `${API}/api/stripe/create-checkout`,
        {
          gameId: Number(gameId),
          packType: pack,
          sessions: Number(sessions),
          date: date || null,
          isBeta: isBeta,
          couponId: appliedCoupon || undefined,
        },
        { headers: { Authorization: `Bearer ${storedToken}` } }
      );

      window.location.href = res.data.url;
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || "Erreur lors de la création du paiement.");
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold text-slate-900">Pack &amp; réservation</p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Ajustez le volume : le tarif s&apos;adapte automatiquement.
          </p>
        </div>

        <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700">
          <Sparkles className="h-3.5 w-3.5 text-orange-500" />
          {tierLabel}
        </span>
      </div>

      {/* Type de pack */}
      <div>
        <p className="text-xs font-medium text-slate-500 mb-1">Type de pack</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setPack("normal")}
            className={`relative flex flex-col items-start rounded-xl border px-3 py-2.5 text-left text-xs sm:text-sm transition ${pack === "normal"
                ? "border-indigo-500 bg-indigo-50 text-indigo-900 shadow-sm"
                : "border-slate-200 bg-slate-50 hover:border-indigo-300"
              }`}
          >
            <span className="font-semibold">Découverte — Journée</span>
            <span className="text-[11px] text-slate-600 mt-0.5">
              Session jouable uniquement le jour choisi (jusqu'à 23:59).
            </span>
          </button>

          <button
            type="button"
            onClick={() => setPack("extended")}
            className={`relative flex flex-col items-start rounded-xl border px-3 py-2.5 text-left text-xs sm:text-sm transition ${pack === "extended"
                ? "border-indigo-500 bg-indigo-50 text-indigo-900 shadow-sm"
                : "border-slate-200 bg-slate-50 hover:border-indigo-300"
              }`}
          >
            <span className="font-semibold">Étendu — Semaine</span>
            <span className="text-[11px] text-slate-600 mt-0.5">
              Session utilisable une fois n'importe quand dans la période de 7 jours sélectionnée & créateur d'équipe.
            </span>
          </button>
        </div>
      </div>

      {/* Nombre de sessions */}
      <div>
        <p className="text-xs font-medium text-slate-500 mb-1">Nombre de sessions</p>
        <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
          <button
            type="button"
            onClick={() => setSessions((s) => Math.max(1, s - 1))}
            className="px-2 py-1 text-lg text-slate-600 hover:text-slate-900"
            aria-label="Diminuer"
          >
            −
          </button>
          <div className="text-center">
            <div className="text-base font-semibold text-slate-900">{sessions}</div>
            <div className="text-[11px] text-slate-500">sessions</div>
          </div>
          <button
            type="button"
            onClick={() => setSessions((s) => Math.min(50, s + 1))}
            className="px-2 py-1 text-lg text-slate-600 hover:text-slate-900"
            aria-label="Augmenter"
          >
            +
          </button>
        </div>

        <div className="mt-2 flex items-center justify-between gap-3 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <BadgePercent className="h-3.5 w-3.5 text-slate-400" />
            Paliers : 2–4, 5–9, 10+.
          </span>
          {savingsTotal > 0 ? (
            <span className="text-slate-700">
              Économie estimée :{" "}
              <span className="font-semibold text-orange-600">
                {savingsTotal.toFixed(0)} €
              </span>
            </span>
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </div>
      </div>

      {/* Date optionnelle */}
      <div>
        <p className="text-xs font-medium text-slate-500 mb-1">
          Date de session (optionnelle)
        </p>
        <div className="relative w-full">
          <DatePicker
            selected={date}
            onChange={(d) => setDate(d)}
            dateFormat="dd/MM/yyyy"
            locale={fr}
            placeholderText="Choisir une date"
            minDate={new Date()}
            dayClassName={(d) =>
              isBefore(startOfDay(d), startOfDay(new Date()))
                ? "opacity-40 text-gray-500 cursor-not-allowed"
                : "text-white"
            }
            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 pl-10 pr-10 text-sm text-slate-800 placeholder:text-slate-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none"
            calendarClassName="dark-calendar"
            popperPlacement="bottom-start"
            wrapperClassName="w-full"
          />
          <CalendarDays className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <span className="pointer-events-none absolute right-3 top-2.5 text-slate-400">⌁</span>
        </div>
        <p className="mt-1 text-[11px] text-slate-500">
          Vous pourrez aussi planifier plus tard depuis votre tableau de bord.
        </p>
      </div>

      {/* ── Code promo ── */}
      <div>
        <p className="text-xs font-medium text-slate-500 mb-1 flex items-center gap-1.5">
          <Tag className="h-3.5 w-3.5" />
          Code promo (optionnel - sensible à la casse)
        </p>

        {appliedCoupon && couponState?.valid ? (
          // Coupon appliqué
          <div className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
              <div>
                <span className="text-xs font-semibold text-emerald-700">{couponState.name}</span>
                <span className="ml-2 text-xs text-emerald-600">{couponState.discount}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={handleRemoveCoupon}
              className="text-slate-400 hover:text-slate-600 transition"
              aria-label="Retirer le coupon"
            >
              <XCircle className="h-4 w-4" />
            </button>
          </div>
        ) : (
          // Champ de saisie
          <div className="flex gap-2">
            <input
              type="text"
              value={couponInput}
              onChange={(e) => { setCouponInput(e.target.value); setCouponState(null); }}
              onKeyDown={(e) => { if (e.key === "Enter") handleCheckCoupon(); }}
              placeholder="Ex : PROMO2024"
              className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none font-mono tracking-widest"
              disabled={couponLoading}
            />
            <button
              type="button"
              onClick={handleCheckCoupon}
              disabled={!couponInput.trim() || couponLoading}
              className="rounded-xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center gap-1.5"
            >
              {couponLoading
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : "Appliquer"
              }
            </button>
          </div>
        )}

        {/* Message d'erreur coupon */}
        {couponState?.error && (
          <p className="mt-1.5 text-[11px] text-red-500 flex items-center gap-1">
            <XCircle className="h-3.5 w-3.5 flex-shrink-0" />
            {couponState.error}
          </p>
        )}
      </div>

      {/* Récap prix */}
      <div className="rounded-2xl bg-slate-900 text-slate-50 px-4 py-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-indigo-200/90">
              Total HT estimé
            </p>
            <p className="text-2xl font-semibold mt-1">{total.toFixed(2)} €</p>
            <p className="text-[11px] text-indigo-100/90 mt-0.5">
              {sessions} session{sessions > 1 ? "s" : ""} ×{" "}
              <span className="font-semibold text-white">
                {unit.toFixed(2)} €
              </span>{" "}
              / session
              {savingsPerSession > 0 ? (
                <>
                  {" "}
                  <span className="text-orange-300">
                    (−{savingsPerSession.toFixed(0)} €/session)
                  </span>
                </>
              ) : null}
            </p>
            {appliedCoupon && couponState?.valid && (
              <p className="text-[11px] text-emerald-300 mt-1 flex items-center gap-1">
                <Tag className="h-3 w-3" />
                Code promo appliqué : {couponState.discount}
              </p>
            )}
          </div>
          <div className="text-[11px] text-slate-200 text-right">
            <p>Jusqu&apos;à 6 joueurs / session.</p>
            <p className="text-slate-300/90">Paiement sécurisé.</p>
          </div>
        </div>
        <div className="mt-3 h-px w-full bg-gradient-to-r from-transparent via-orange-400/35 to-transparent" />
      </div>

      {/* CTA */}
      <div className="pt-1">
        {token ? (
          <button
            onClick={handleBuy}
            className="group w-full rounded-full bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition relative overflow-hidden"
          >
            <span
              className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition"
              style={{
                background:
                  "radial-gradient(circle at 30% 0%, rgba(249,115,22,0.35) 0, transparent 55%)",
              }}
            />
            <span className="relative inline-flex items-center justify-center gap-2">
              <CreditCard className="h-4 w-4" />
              Procéder au paiement
              <span className="inline-block h-2 w-2 rounded-full bg-orange-400/0 group-hover:bg-orange-400/80 transition" />
            </span>
          </button>
        ) : (
          <button
            onClick={() => navigate(`/login?next=${buildNext()}`)}
            className="w-full rounded-full bg-slate-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-slate-500 transition"
          >
            <span className="inline-flex items-center justify-center gap-2">
              <Lock className="h-4 w-4" />
              Connectez-vous pour acheter
            </span>
          </button>
        )}

        <p className="mt-2 text-[11px] text-slate-500 text-center flex items-center justify-center gap-2">
          Paiement sécurisé via Stripe
        </p>
      </div>

      <p className="mt-2 text-[11px] text-slate-500 leading-relaxed">
        {pack === "extended" ? (
          <>
            <span className="font-medium text-slate-700">Étendu :</span>{" "}
            vous activez une session une fois, puis elle est utilisable <span className="font-medium text-slate-700">une fois</span>, n'importe quand sur une période de{" "}
            <span className="font-medium text-slate-700">7 jours</span>{" "}
            (jour d'activation + 6 jours). Idéal si vous avez besoin de flexibilité pour l'organisation.
          </>
        ) : (
          <>
            <span className="font-medium text-slate-700">Journée :</span>{" "}
            une session est jouable uniquement le jour planifié (jusqu'à 23:59).
          </>
        )}
      </p>
    </div>
  );
}

export default PackageSelector;